import React, { useEffect, useRef, useState } from 'react';
import FlexComponent from '../FlexComponent';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Avatar } from 'primereact/avatar';
import { ErrorMessage } from 'formik';
import './form.css';

function CosignerWorkHistoryComponent({
  values,
  errors,
  touched,
  handleBlur,
  setFieldValue,
  index,
  isNewEntry,
  currentCosigner,
  handleNewEntryChange,
  isSecondJob,
  toast,
}) {
  // Initialize error state with separate objects for new entry and existing work history
  const [error, setError] = useState({ newEntry: {}, workHistory: [] });
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentWork, setCurrentWork] = useState({
    title: '',
    yearly_income: '',
    years_in_industry: '',
    company_name: '',
    employment_type: 'Salary - W2 Employee',
    status: '',
    start_date: null,
    end_date: null,
    full_address: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    industry_type: '',
    monthly_overtime: '',
    monthly_bonuses: '',
    monthly_commissions: '',
    is_second_job: isSecondJob,
  });
  const [currentGapExplanation, setCurrentGapExplanation] = useState(
    isNewEntry ? currentCosigner?.work_history_gap_explanation || '' : values.cosigner_list[index]?.work_history_gap_explanation || '',
  );
  const [addMoreWorkHistory, setAddMoreWorkHistory] = useState(false);
  const autoCompleteRef = useRef(null);
  const inputRef = useRef(null);

  // Safely access work_history
  const workHistory = isNewEntry ? currentCosigner?.work_history || [] : values.cosigner_list[index]?.work_history || [];

  useEffect(() => {
    if (window.google && window.google.maps) {
      autoCompleteRef.current = null;
      if (!inputRef.current || !(inputRef.current instanceof HTMLInputElement)) return;
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['geocode'],
        componentRestrictions: { country: 'us' },
      });

      const listener = autoCompleteRef.current.addListener('place_changed', () => {
        const place = autoCompleteRef.current.getPlace();
        console.log('Place selected:', place);

        let streetNumber = '';
        let route = '';
        const updatedAddress = {
          full_address: place.formatted_address || '',
          street_address: '',
          city: '',
          state: '',
          zip_code: '',
        };

        (place.address_components || []).forEach((component) => {
          const types = component.types;
          if (types.includes('street_number')) streetNumber = component.long_name;
          if (types.includes('route')) route = component.long_name;
          if (types.includes('locality')) updatedAddress.city = component.long_name;
          if (types.includes('administrative_area_level_1')) updatedAddress.state = component.long_name;
          if (types.includes('postal_code')) updatedAddress.zip_code = component.long_name;
        });

        if (streetNumber || route) updatedAddress.street_address = `${streetNumber} ${route}`.trim();

        console.log('Updated address:', updatedAddress);
        setCurrentWork((prev) => {
          const newState = { ...prev, ...updatedAddress };
          console.log('New work state:', newState);
          // Re-validate on address change
          const validationErrors = validateWorkEntry(newState);
          setError((prevError) => ({ ...prevError, newEntry: validationErrors }));
          return newState;
        });
      });

      return () => {
        if (autoCompleteRef.current && listener) {
          window.google.maps.event.removeListener(listener);
        }
      };
    } else {
      console.error('Google Maps API not loaded');
    }
  }, [addMoreWorkHistory]);

  const calculateTotalWorkDuration = () => {
    if (!workHistory.length) return 0;
    let totalMonths = 0;
    workHistory.forEach((work) => {
      if (work.start_date && work.is_second_job === isSecondJob) {
        const start = new Date(work.start_date);
        const end = work.status === 'true' || !work.end_date ? new Date() : new Date(work.end_date);
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        totalMonths += months > 0 ? months : 0;
      }
    });
    return totalMonths;
  };

  const formatDuration = (months) => {
    if (!months) return '';
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    let result = '';
    if (years > 0) result += `${years} year${years > 1 ? 's' : ''}`;
    if (remainingMonths > 0) result += `${result ? ' ' : ''}${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
    return result || '0 months';
  };

  const detectWorkHistoryGaps = () => {
    const filteredHistory = workHistory.filter((work) => work.is_second_job === isSecondJob);
    if (filteredHistory.length < 2) return [];
    const sortedHistory = [...filteredHistory].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    const gaps = [];
    for (let i = 0; i < sortedHistory.length - 1; i++) {
      const currentEnd = sortedHistory[i].status === 'true' || !sortedHistory[i].end_date ? new Date() : new Date(sortedHistory[i].end_date);
      const nextStart = sortedHistory[i + 1].start_date ? new Date(sortedHistory[i + 1].start_date) : null;
      if (currentEnd && nextStart && currentEnd < nextStart) {
        const gapMonths = (nextStart.getFullYear() - currentEnd.getFullYear()) * 12 + (nextStart.getMonth() - currentEnd.getMonth());
        if (gapMonths > 1) {
          gaps.push({
            from: currentEnd,
            to: nextStart,
            months: gapMonths,
          });
        }
      }
    }
    return gaps;
  };

  const validateWorkEntry = (workEntry) => {
    const errors = {};
    if (!workEntry.title?.trim()) errors.title = true;
    if (!workEntry.yearly_income) errors.yearly_income = true;
    if (!workEntry.years_in_industry) errors.years_in_industry = true;
    if (!workEntry.status) errors.status = true;
    if (workEntry.status === 'false' && !workEntry.start_date) {
      errors.start_date = true;
    }
    if (workEntry.status === 'false' && !workEntry.end_date) {
      errors.end_date = true;
    }
    if (workEntry.status === 'true' && !workEntry.start_date) {
      errors.start_date = true;
    }
    return errors;
  };

  const handleAddClick = () => {
    // console.log('handleAddClick called, toast:', toast, 'currentCosigner:', currentCosigner);
    const validationErrors = validateWorkEntry(currentWork);
    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      const updatedWorkHistory = [...workHistory, { ...currentWork }];
      if (isNewEntry) {
        handleNewEntryChange('work_history', updatedWorkHistory);
      } else {
        setFieldValue(`cosigner_list.${index}.work_history`, updatedWorkHistory);
      }
      if (currentGapExplanation) {
        if (isNewEntry) {
          handleNewEntryChange('work_history_gap_explanation', currentGapExplanation);
        } else {
          setFieldValue(`cosigner_list.${index}.work_history_gap_explanation`, currentGapExplanation);
        }
      }
      setCurrentWork({
        title: '',
        yearly_income: '',
        years_in_industry: '',
        company_name: '',
        employment_type: 'Salary - W2 Employee',
        status: '',
        start_date: null,
        end_date: null,
        full_address: '',
        street_address: '',
        city: '',
        state: '',
        zip_code: '',
        industry_type: '',
        monthly_overtime: '',
        monthly_bonuses: '',
        monthly_commissions: '',
        is_second_job: isSecondJob,
      });
      setCurrentGapExplanation('');
      setError((prev) => ({ ...prev, newEntry: {} }));
      setAddMoreWorkHistory(false);
      setLoading(false);
    } else {
      setError((prev) => ({ ...prev, newEntry: validationErrors }));
    }
  };

  const handleEditClick = (idx) => {
    setEditingIndex(idx);
    // Initialize errors for this index if not already present
    setError((prev) => {
      const newWorkHistoryErrors = [...prev.workHistory];
      newWorkHistoryErrors[idx] = newWorkHistoryErrors[idx] || {};
      return { ...prev, workHistory: newWorkHistoryErrors };
    });
  };

  const handleSaveEdit = (idx) => {
    const work = workHistory[idx];
    const validationErrors = validateWorkEntry(work);
    if (Object.keys(validationErrors).length === 0) {
      setEditingIndex(null);
      setError((prev) => {
        const newWorkHistoryErrors = [...prev.workHistory];
        newWorkHistoryErrors[idx] = {};
        return { ...prev, workHistory: newWorkHistoryErrors };
      });
    } else {
      setError((prev) => {
        const newWorkHistoryErrors = [...prev.workHistory];
        newWorkHistoryErrors[idx] = validationErrors;
        return { ...prev, workHistory: newWorkHistoryErrors };
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setError((prev) => {
      const newWorkHistoryErrors = [...prev.workHistory];
      newWorkHistoryErrors[editingIndex] = {};
      return { ...prev, workHistory: newWorkHistoryErrors };
    });
  };

  const handleDeleteClick = (idx) => {
    const updatedWorkHistory = workHistory.filter((_, i) => i !== idx);
    if (isNewEntry) {
      handleNewEntryChange('work_history', updatedWorkHistory);
    } else {
      setFieldValue(`cosigner_list.${index}.work_history`, updatedWorkHistory);
    }
    setError((prev) => {
      const newWorkHistoryErrors = [...prev.workHistory];
      newWorkHistoryErrors.splice(idx, 1); // Remove errors for deleted entry
      return { ...prev, workHistory: newWorkHistoryErrors };
    });
    if (workHistory.length === 1) {
      if (isNewEntry) {
        handleNewEntryChange('work_history_gap_explanation', '');
      } else {
        setFieldValue(`cosigner_list.${index}.work_history_gap_explanation`, '');
      }
      setCurrentGapExplanation('');
    }
  };

  const handleInputChange = (idx, field, value) => {
    const updatedWorkHistory = [...workHistory];
    updatedWorkHistory[idx] = { ...updatedWorkHistory[idx], [field]: value };
    if (isNewEntry) {
      handleNewEntryChange('work_history', updatedWorkHistory);
    } else {
      setFieldValue(`cosigner_list.${index}.work_history`, updatedWorkHistory);
    }
    // Re-validate the entry on change
    const validationErrors = validateWorkEntry(updatedWorkHistory[idx]);
    setError((prev) => {
      const newWorkHistoryErrors = [...prev.workHistory];
      newWorkHistoryErrors[idx] = validationErrors;
      return { ...prev, workHistory: newWorkHistoryErrors };
    });
  };

  const handleNewEntryChangeLocal = (field, value) => {
    setCurrentWork((prev) => {
      const newState = { ...prev, [field]: value };
      // Re-validate on change
      const validationErrors = validateWorkEntry(newState);
      setError((prevError) => ({ ...prevError, newEntry: validationErrors }));
      return newState;
    });
  };

  const changeAvatarColor = (type) => {
    if (!type) return '#8C8C8C';
    if (type.includes('W2')) return '#F141D0';
    if (type === '1099 Independent Contractor') return '#418BCC';
    return '#EB4243';
  };

  const avatarTitle = (type) => {
    if (!type) return '';
    if (type.includes('W2')) return 'W2';
    if (type === '1099 Independent Contractor') return '1099';
    if (type === 'Self Employed/Business Owner') return 'SE';
    return '';
  };

  const totalWorkDuration = calculateTotalWorkDuration();
  const gaps = detectWorkHistoryGaps();
  const showNewEntryForm = totalWorkDuration < 24; // 2 years = 24 months
  const isCurrentStatusTrue = workHistory?.some((work) => work.status === 'true') && !isSecondJob;

  return (
    <div style={{ width: '100%', padding: '0 1.5rem' }} id="workAndIncome">
      {!isSecondJob && (
        <div>
          <p style={{ fontSize: '20px', fontWeight: 'bold', padding: '0.75rem' }}>
            What do you do for work?
          </p>
        </div>
      )}

      {workHistory.length > 0 && (
        <div>
          {workHistory
            .map((work, idx) => ({ work, idx }))
            .filter(({ work }) => work.is_second_job === isSecondJob)
            .map(({ work, idx }) => (
              <div key={idx} style={{ width: '100%' }}>
                <FlexComponent
                  style={{ background: 'white', margin: '1rem 0', padding: '1rem', borderRadius: '10px' }}
                  gap="1rem"
                  flexDirection="column"
                >
                  {editingIndex !== idx ? (
                    <FlexComponent alignItems="start" justifyContent="start" gap="1rem" style={{ textAlign: 'start' }}>
                      <div>
                        <Avatar
                          style={{ width: 80, color: 'white', background: changeAvatarColor(work?.employment_type) }}
                          label={avatarTitle(work?.employment_type)}
                          size="xlarge"
                        />
                      </div>
                      <div style={{ width: '50%' }}>
                        <p>{work.title || 'No Title'}</p>
                        <p>{work.employment_type || 'No Employment Type'}</p>
                        <p style={{ color: '#8C8C8C' }}>{work.years_in_industry || 'No Time at Position'}</p>
                        <p>Start Date: {work.start_date ? new Date(work.start_date).toLocaleDateString() : 'N/A'}</p>
                        {!work.status && work.end_date && (
                          <p>End Date: {new Date(work.end_date).toLocaleDateString() || 'N/A'}</p>
                        )}
                        <p>
                          <span style={{ fontSize: '22px', fontWeight: 'bold' }}>${work.yearly_income || '0'}</span>
                          <span style={{ marginLeft: '.3rem' }}>Yearly</span>
                        </p>
                      </div>
                      <Button type="button" onClick={() => handleEditClick(idx)}>
                        Edit
                      </Button>
                      {workHistory.length > 1 && (
                        <Button type="button" onClick={() => handleDeleteClick(idx)}>
                          Delete
                        </Button>
                      )}
                    </FlexComponent>
                  ) : (
                    <FlexComponent flexDirection="column" gap="1rem" alignItems="start" style={{ width: '100%' }}>
                      <FlexComponent
                        justifyContent="start"
                        className="inputContainerDiv inputFlexContainer"
                        gap="1rem"
                        alignItems="start"
                        style={{ width: '100%' }}
                      >
                        <div
                          className="inputContainerDiv"
                          style={{ width: '45%', border: error.workHistory[idx]?.title && '1px solid red', borderRadius: '5px' }}
                        >
                          <InputText
                            value={work.title || ''}
                            onChange={(e) => handleInputChange(idx, 'title', e.target.value)}
                            style={{ width: '100%' }}
                            placeholder="Position/Title"
                          />
                        </div>
                        <div className="inputContainerDiv" style={{ width: '30%' }}>
                          <InputText
                            value={work.company_name || ''}
                            onChange={(e) => handleInputChange(idx, 'company_name', e.target.value)}
                            style={{ width: '100%' }}
                            placeholder="Employer/Company Name"
                          />
                        </div>
                        <div
                          className="inputContainerDiv"
                          style={{ width: '25%', border: error.workHistory[idx]?.status && '1px solid red', borderRadius: '5px' }}
                        >
                          <Dropdown
                            value={work.status || ''}
                            options={
                              workHistory[0].status === 'true' && idx > 0
                                ? [{ label: 'I used to work in this role', value: 'false' }]
                                : [
                                  { label: 'Currently working in this role', value: 'true' },
                                  { label: 'I used to work in this role', value: 'false' },
                                ]
                            }
                            onChange={(e) => handleInputChange(idx, 'status', e.value)}
                            style={{ width: '100%' }}
                            placeholder="Status"
                          />
                        </div>
                      </FlexComponent>

                      <FlexComponent
                        className="inputFlexContainer"
                        justifyContent="start"
                        gap="1rem"
                        alignItems="start"
                        style={{ width: '100%' }}
                      >
                        <div className="inputContainerDiv" style={{ width: '40%' }}>
                          <InputText
                            value={work.full_address || ''}
                            onChange={(e) => handleInputChange(idx, 'full_address', e.target.value)}
                            style={{ width: '100%' }}
                            placeholder="Enter Address"
                          />
                        </div>
                      </FlexComponent>

                      <FlexComponent
                        className="inputFlexContainer"
                        justifyContent="start"
                        gap="1rem"
                        alignItems="start"
                        style={{ width: '100%' }}
                      >
                        <div
                          className="inputContainerDiv"
                          style={{ width: '20%', border: error.workHistory[idx]?.start_date && '1px solid red', borderRadius: '5px' }}
                        >
                          <div className="p-inputgroup flex-1 login-input-div">
                            <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                              <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                            </span>
                            <Calendar
                              value={work.start_date ? new Date(work.start_date) : null}
                              onChange={(e) => handleInputChange(idx, 'start_date', e.value)}
                              style={{ width: '100%', borderLeft: 'none' }}
                              placeholder="Start Date"
                              className="inputWithIcon"
                            />
                          </div>
                        </div>
                        {work.status === 'false' && (
                          <div
                            className="inputContainerDiv"
                            style={{ width: '20%', border: error.workHistory[idx]?.end_date && '1px solid red', borderRadius: '5px' }}
                          >
                            <div className="p-inputgroup flex-1 login-input-div">
                              <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                                <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                              </span>
                              <Calendar
                                value={work.end_date ? new Date(work.end_date) : null}
                                onChange={(e) => handleInputChange(idx, 'end_date', e.value)}
                                style={{ width: '100%', borderLeft: 'none' }}
                                placeholder="End Date"
                                className="inputWithIcon"
                              />
                            </div>
                          </div>
                        )}
                        <div
                          className="inputContainerDiv"
                          style={{ width: '20%', border: error.workHistory[idx]?.yearly_income && '1px solid red', borderRadius: '5px' }}
                        >
                          <div className="p-inputgroup flex-1 login-input-div">
                            <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                              <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                            </span>
                            <InputText
                              type="number"
                              value={work.yearly_income || ''}
                              onChange={(e) => handleInputChange(idx, 'yearly_income', e.target.value)}
                              style={{ width: '100%', borderLeft: 'none' }}
                              placeholder="Yearly Income"
                              className="inputWithIcon"
                            />
                          </div>
                        </div>
                        <div className="inputContainerDiv" style={{ width: '20%' }}>
                          <Dropdown
                            value={work.employment_type || ''}
                            options={[
                              { label: 'Salary - W2 Employee', value: 'Salary - W2 Employee' },
                              { label: 'Hourly - W2 Employee', value: 'Hourly - W2 Employee' },
                              { label: '1099 Independent Contractor', value: '1099 Independent Contractor' },
                              { label: 'Self Employed/Business Owner', value: 'Self Employed/Business Owner' },
                              { label: 'Retired', value: 'Retired' },
                            ]}
                            onChange={(e) => handleInputChange(idx, 'employment_type', e.value)}
                            style={{ width: '100%' }}
                            placeholder="Income Type"
                          />
                        </div>
                      </FlexComponent>

                      <FlexComponent
                        className="inputFlexContainer"
                        gap="1rem"
                        alignItems="start"
                        justifyContent="start"
                        style={{ width: '100%' }}
                      >
                        <div className="inputContainerDiv" style={{ width: '30%' }}>
                          <Dropdown
                            value={work.industry_type || ''}
                            options={[
                              { label: 'Healthcare', value: 'Healthcare' },
                              { label: 'Construction & Trades', value: 'Construction & Trades' },
                              { label: 'Education', value: 'Education' },
                              { label: 'Retail & E-commerce', value: 'Retail & E-commerce' },
                              { label: 'Food & Hospitality', value: 'Food & Hospitality' },
                              { label: 'Finance & Insurance', value: 'Finance & Insurance' },
                              { label: 'Real Estate', value: 'Real Estate' },
                              { label: 'Government & Military', value: 'Government & Military' },
                              { label: 'Transportation & Logistics', value: 'Transportation & Logistics' },
                              { label: 'Manufacturing', value: 'Manufacturing' },
                              { label: 'Tech & IT', value: 'Tech & IT' },
                              { label: 'Legal & Law Enforcement', value: 'Legal & Law Enforcement' },
                              { label: 'Media & Marketing', value: 'Media & Marketing' },
                              { label: 'Non-Profit & Religious', value: 'Non-Profit & Religious' },
                              { label: 'Energy & Utilities', value: 'Energy & Utilities' },
                              { label: 'Arts & Entertainment', value: 'Arts & Entertainment' },
                              { label: 'Professional Services', value: 'Professional Services' },
                              { label: 'Self-Employed', value: 'Self-Employed' },
                              { label: 'Unemployed / Retired', value: 'Unemployed / Retired' },
                              { label: 'Other / Unknown', value: 'Other / Unknown' },
                            ]}
                            onChange={(e) => handleInputChange(idx, 'industry_type', e.value)}
                            style={{ width: '100%' }}
                            placeholder="Industry Type"
                          />
                        </div>
                        <div
                          className="inputContainerDiv"
                          style={{ width: '20%', border: error.workHistory[idx]?.years_in_industry && '1px solid red', borderRadius: '5px' }}
                        >
                          <InputText
                            type="number"
                            value={work.years_in_industry || ''}
                            onChange={(e) => handleInputChange(idx, 'years_in_industry', e.target.value)}
                            style={{ width: '100%', borderLeft: 'none' }}
                            placeholder="Years In Industry"
                          />
                        </div>
                      </FlexComponent>

                      <FlexComponent
                        className="inputFlexContainer"
                        justifyContent="start"
                        gap="1rem"
                        alignItems="start"
                        style={{ width: '100%' }}
                      >
                        <div className="inputContainerDiv" style={{ textAlign: 'start', width: '50%' }}>
                          <FlexComponent>
                            <div style={{ fontSize: '14px', marginRight: '10px' }}>
                              Have you received any bonuses/overtime/commissions?
                            </div>
                            <div className="p-inputgroup flex-1 login-input-div">
                              <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                                <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                              </span>
                              <InputText
                                type="number"
                                value={work.monthly_overtime || ''}
                                onChange={(e) => handleInputChange(idx, 'monthly_overtime', e.target.value)}
                                style={{ width: '100%', borderLeft: 'none' }}
                                placeholder="Monthly Overtime"
                                className="inputWithIcon"
                              />
                            </div>
                          </FlexComponent>
                        </div>
                        <div className="inputContainerDiv" style={{ textAlign: 'start', width: '25%' }}>
                          <div className="p-inputgroup flex-1 login-input-div">
                            <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                              <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                            </span>
                            <InputText
                              type="number"
                              value={work.monthly_bonuses || ''}
                              onChange={(e) => handleInputChange(idx, 'monthly_bonuses', e.target.value)}
                              style={{ width: '100%', borderLeft: 'none' }}
                              placeholder="Monthly Bonuses"
                              className="inputWithIcon"
                            />
                          </div>
                        </div>
                        <div className="inputContainerDiv" style={{ textAlign: 'start', width: '25%' }}>
                          <div className="p-inputgroup flex-1 login-input-div">
                            <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                              <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                            </span>
                            <InputText
                              type="number"
                              value={work.monthly_commissions || ''}
                              onChange={(e) => handleInputChange(idx, 'monthly_commissions', e.target.value)}
                              style={{ width: '100%', borderLeft: 'none' }}
                              placeholder="Monthly Commissions"
                              className="inputWithIcon"
                            />
                          </div>
                        </div>
                      </FlexComponent>

                      <FlexComponent justifyContent="center" gap="1rem">
                        <Button type="button" onClick={() => handleSaveEdit(idx)}>
                          Save
                        </Button>
                        <Button type="button" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                      </FlexComponent>
                    </FlexComponent>
                  )}
                </FlexComponent>
              </div>
            ))}
        </div>
      )}

      {(showNewEntryForm || addMoreWorkHistory) && (
        <div style={{ background: 'white', padding: '1rem', borderRadius: '10px', margin: '1rem 0' }}>
          {workHistory.length > 0 && showNewEntryForm && !isSecondJob && (
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>Great. And, what did you do before that?</div>
          )}
          <FlexComponent flexDirection="column" gap="1rem" alignItems="start">
            <FlexComponent
              justifyContent="start"
              className="inputFlexContainer"
              gap="1rem"
              alignItems="start"
              style={{ width: '100%' }}
            >
              <div
                className="inputContainerDiv"
                style={{ width: '45%', border: error.newEntry.title && '1px solid red', borderRadius: '5px' }}
              >
                <InputText
                  value={currentWork.title || ''}
                  onChange={(e) => handleNewEntryChangeLocal('title', e.target.value)}
                  style={{ width: '100%' }}
                  placeholder="Position/Title"
                />
              </div>
              <div className="inputContainerDiv" style={{ width: '30%' }}>
                <InputText
                  value={currentWork.company_name || ''}
                  onChange={(e) => handleNewEntryChangeLocal('company_name', e.target.value)}
                  style={{ width: '100%' }}
                  placeholder="Employer/Company Name"
                />
              </div>
              <div
                className="inputContainerDiv"
                style={{ width: '25%', border: error.newEntry.status && '1px solid red', borderRadius: '5px' }}
              >
                <Dropdown
                  value={currentWork.status || ''}
                  options={
                    isCurrentStatusTrue
                      ? [{ label: 'I used to work in this role', value: 'false' }]
                      : [
                        { label: 'Currently working in this role', value: 'true' },
                        { label: 'I used to work in this role', value: 'false' },
                      ]
                  }
                  onChange={(e) => handleNewEntryChangeLocal('status', e.value)}
                  style={{ width: '100%' }}
                  placeholder="Status"
                />
              </div>
            </FlexComponent>

            <FlexComponent
              className="inputFlexContainer"
              justifyContent="start"
              gap="1rem"
              alignItems="start"
              style={{ width: '100%' }}
            >
              <div className="inputContainerDiv" style={{ width: '40%' }}>
                <InputText
                  ref={inputRef}
                  value={currentWork.full_address || ''}
                  onChange={(e) => handleNewEntryChangeLocal('full_address', e.target.value)}
                  style={{ width: '100%' }}
                  placeholder="Enter Address"
                />
              </div>
            </FlexComponent>

            <FlexComponent
              className="inputFlexContainer"
              justifyContent="start"
              gap="1rem"
              alignItems="start"
              style={{ width: '100%' }}
            >
              <div
                className="inputContainerDiv"
                style={{ width: '20%', border: error.newEntry.start_date && '1px solid red', borderRadius: '5px' }}
              >
                <div className="p-inputgroup flex-1 login-input-div">
                  <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                    <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                  </span>
                  <Calendar
                    value={currentWork.start_date ? new Date(currentWork.start_date) : null}
                    onChange={(e) => handleNewEntryChangeLocal('start_date', e.value)}
                    style={{ width: '100%', borderLeft: 'none' }}
                    placeholder="Start Date"
                    className="inputWithIcon"
                  />
                </div>
              </div>
              {currentWork.status === 'false' && (
                <div
                  className="inputContainerDiv"
                  style={{ width: '20%', border: error.newEntry.end_date && '1px solid red', borderRadius: '5px' }}
                >
                  <div className="p-inputgroup flex-1 login-input-div">
                    <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                      <i className="pi pi-calendar" style={{ color: 'black', fontWeight: '600', padding: '5px' }}></i>
                    </span>
                    <Calendar
                      value={currentWork.end_date ? new Date(currentWork.end_date) : null}
                      onChange={(e) => handleNewEntryChangeLocal('end_date', e.value)}
                      style={{ width: '100%', borderLeft: 'none' }}
                      placeholder="End Date"
                      className="inputWithIcon"
                    />
                  </div>
                </div>
              )}
              <div
                className="inputContainerDiv"
                style={{ width: '20%', border: error.newEntry.yearly_income && '1px solid red', borderRadius: '5px' }}
              >
                <div className="p-inputgroup flex-1 login-input-div">
                  <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                    <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                  </span>
                  <InputText
                    type="number"
                    value={currentWork.yearly_income || ''}
                    onChange={(e) => handleNewEntryChangeLocal('yearly_income', e.target.value)}
                    style={{ width: '100%', borderLeft: 'none' }}
                    placeholder="Yearly Income"
                    className="inputWithIcon"
                  />
                </div>
              </div>
              <div className="inputContainerDiv" style={{ width: '20%' }}>
                <Dropdown
                  value={currentWork.employment_type || ''}
                  options={[
                    { label: 'Salary - W2 Employee', value: 'Salary - W2 Employee' },
                    { label: 'Hourly - W2 Employee', value: 'Hourly - W2 Employee' },
                    { label: '1099 Independent Contractor', value: '1099 Independent Contractor' },
                    { label: 'Self Employed/Business Owner', value: 'Self Employed/Business Owner' },
                    { label: 'Retired', value: 'Retired' },
                  ]}
                  onChange={(e) => handleNewEntryChangeLocal('employment_type', e.value)}
                  style={{ width: '100%' }}
                  placeholder="Income Type"
                />
              </div>
            </FlexComponent>

            <FlexComponent
              className="inputFlexContainer"
              gap="1rem"
              alignItems="start"
              justifyContent="start"
              style={{ width: '100%' }}
            >
              <div className="inputContainerDiv" style={{ width: '30%' }}>
                <Dropdown
                  value={currentWork.industry_type || ''}
                  options={[
                    { label: 'Healthcare', value: 'Healthcare' },
                    { label: 'Construction & Trades', value: 'Construction & Trades' },
                    { label: 'Education', value: 'Education' },
                    { label: 'Retail & E-commerce', value: 'Retail & E-commerce' },
                    { label: 'Food & Hospitality', value: 'Food & Hospitality' },
                    { label: 'Finance & Insurance', value: 'Finance & Insurance' },
                    { label: 'Real Estate', value: 'Real Estate' },
                    { label: 'Government & Military', value: 'Government & Military' },
                    { label: 'Transportation & Logistics', value: 'Transportation & Logistics' },
                    { label: 'Manufacturing', value: 'Manufacturing' },
                    { label: 'Tech & IT', value: 'Tech & IT' },
                    { label: 'Legal & Law Enforcement', value: 'Legal & Law Enforcement' },
                    { label: 'Media & Marketing', value: 'Media & Marketing' },
                    { label: 'Non-Profit & Religious', value: 'Non-Profit & Religious' },
                    { label: 'Energy & Utilities', value: 'Energy & Utilities' },
                    { label: 'Arts & Entertainment', value: 'Arts & Entertainment' },
                    { label: 'Professional Services', value: 'Professional Services' },
                    { label: 'Self-Employed', value: 'Self-Employed' },
                    { label: 'Unemployed / Retired', value: 'Unemployed / Retired' },
                    { label: 'Other / Unknown', value: 'Other / Unknown' },
                  ]}
                  onChange={(e) => handleNewEntryChangeLocal('industry_type', e.value)}
                  style={{ width: '100%' }}
                  placeholder="Industry Type"
                />
              </div>
              <div
                className="inputContainerDiv"
                style={{ width: '20%', border: error.newEntry.years_in_industry && '1px solid red', borderRadius: '5px' }}
              >
                <InputText
                  type="number"
                  value={currentWork.years_in_industry || ''}
                  onChange={(e) => handleNewEntryChangeLocal('years_in_industry', e.target.value)}
                  style={{ width: '100%', borderLeft: 'none' }}
                  placeholder="Years In Industry"
                />
              </div>
            </FlexComponent>

            <FlexComponent
              className="inputFlexContainer"
              justifyContent="start"
              gap="1rem"
              alignItems="start"
              style={{ width: '100%' }}
            >
              <div className="inputContainerDiv" style={{ textAlign: 'start', width: '50%' }}>
                <FlexComponent>
                  <div style={{ fontSize: '14px', marginRight: '10px' }}>
                    Have you received any bonuses/overtime/commissions?
                  </div>
                  <div className="p-inputgroup flex-1 login-input-div">
                    <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                      <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                    </span>
                    <InputText
                      type="number"
                      value={currentWork.monthly_overtime || ''}
                      onChange={(e) => handleNewEntryChangeLocal('monthly_overtime', e.target.value)}
                      style={{ width: '100%', borderLeft: 'none' }}
                      placeholder="Monthly Overtime"
                      className="inputWithIcon"
                    />
                  </div>
                </FlexComponent>
              </div>
              <div className="inputContainerDiv" style={{ textAlign: 'start', width: '25%' }}>
                <div className="p-inputgroup flex-1 login-input-div">
                  <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                    <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                  </span>
                  <InputText
                    type="number"
                    value={currentWork.monthly_bonuses || ''}
                    onChange={(e) => handleNewEntryChangeLocal('monthly_bonuses', e.target.value)}
                    style={{ width: '100%', borderLeft: 'none' }}
                    placeholder="Monthly Bonuses"
                    className="inputWithIcon"
                  />
                </div>
              </div>
              <div className="inputContainerDiv" style={{ textAlign: 'start', width: '25%' }}>
                <div className="p-inputgroup flex-1 login-input-div">
                  <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                    <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                  </span>
                  <InputText
                    type="number"
                    value={currentWork.monthly_commissions || ''}
                    onChange={(e) => handleNewEntryChangeLocal('monthly_commissions', e.target.value)}
                    style={{ width: '100%', borderLeft: 'none' }}
                    placeholder="Monthly Commissions"
                    className="inputWithIcon"
                  />
                </div>
              </div>
            </FlexComponent>

            <FlexComponent justifyContent="center" gap="1rem">
              <FlexComponent flexDirection="column" >
                <Button
                  type="button"
                  label="Save Work History"
                  style={{ borderRadius: '5px', fontSize: '16px', padding: '10px', border: '1px solid #E0E4EC', background: Object.keys(validateWorkEntry(currentWork)).length === 0 ? "red" : '#0F0F0F', color: 'white' }}
                  onClick={handleAddClick}
                  disabled={loading}
                />
                {Object.keys(validateWorkEntry(currentWork)).length === 0 && <div style={{ fontSize: "16px", marginTop: ".5rem" }} >Clcik here to Save Work History</div>}
              </FlexComponent>
              {(workHistory.length > 0 || addMoreWorkHistory) && (
                <Button
                  type="button"
                  label="Cancel"
                  style={{ borderRadius: '5px', fontSize: '16px', padding: '5px 20px', border: '1px solid #E0E4EC', background: 'white', color: '#8C8C8C' }}
                  onClick={() => {
                    setAddMoreWorkHistory(false);
                    setCurrentWork({
                      title: '',
                      yearly_income: '',
                      years_in_industry: '',
                      company_name: '',
                      employment_type: 'Salary - W2 Employee',
                      status: '',
                      start_date: null,
                      end_date: null,
                      full_address: '',
                      street_address: '',
                      city: '',
                      state: '',
                      zip_code: '',
                      industry_type: '',
                      monthly_overtime: '',
                      monthly_bonuses: '',
                      monthly_commissions: '',
                      is_second_job: isSecondJob,
                    });
                    setCurrentGapExplanation('');
                    setError((prev) => ({ ...prev, newEntry: {} }));
                  }}
                />
              )}
            </FlexComponent>
          </FlexComponent>
        </div>
      )}

      {!showNewEntryForm && !addMoreWorkHistory && (
        <div style={{ textAlign: 'center', margin: '1rem' }}>
          <Button
            type="button"
            label="Add More Work History"
            style={{ borderRadius: '5px', fontSize: '16px', padding: '5px 20px', border: '1px solid #E0E4EC', background: 'white', color: '#8C8C8C' }}
            onClick={() => setAddMoreWorkHistory(true)}
          />
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: '1rem', width: '100%' }}>
        <ErrorMessage name={`cosigner_list.${index}.work_history`} component="div" className="error" style={{ color: 'red' }} />
      </div>

      {gaps.length > 0 && (
        <FlexComponent
          className="inputFlexContainer"
          justifyContent="start"
          gap="1rem"
          alignItems="start"
          style={{ width: '100%', marginTop: '1rem' }}
        >
          <div className="inputContainerDiv" style={{ width: '100%' }}>
            <label style={{ fontSize: '20px', fontWeight: 'bold' }}>
              Okay great. It looks like there is a gap of about {formatDuration(gaps[gaps.length - 1].months)}. Can you tell me more about that?
            </label>
            <InputText
              value={currentGapExplanation || ''}
              onChange={(e) => {
                setCurrentGapExplanation(e.target.value);
                if (isNewEntry) {
                  handleNewEntryChange('work_history_gap_explanation', e.target.value);
                } else {
                  setFieldValue(`cosigner_list.${index}.work_history_gap_explanation`, e.target.value);
                }
              }}
              style={{ width: '100%' }}
              placeholder="Explain gap in work history"
            />
          </div>
        </FlexComponent>
      )}
    </div>
  );
}

export default CosignerWorkHistoryComponent;